import { Controller } from "@hotwired/stimulus";

// KeyboardController owns every global keyboard shortcut in the app.
// Mounted on <body> in base.html, it listens at the document level so
// any page (and the modals it opens) inherits the same bindings.
//
// Conventions:
//   - "?" toggles the help modal so the binding list is always one
//     keystroke away. Also fires on Ctrl/Cmd+? for users whose
//     keymap reaches ? only via Shift+/.
//   - Cmd/Ctrl+K opens the command palette modal (#cmd-palette).
//   - "/" opens the command palette, GitHub-style.
//   - "g" then a key in {e,n,r,w} navigates to a fixed page (chord).
//   - Esc closes whichever modal is open (data-modal marker).
//
// Shortcuts that involve a Cmd/Ctrl modifier or the Escape key fire
// even when an input has focus; chord shortcuts and plain letters are
// suppressed inside text inputs, textareas, and contenteditable regions
// so typing a doc never triggers a nav.
export default class KeyboardController extends Controller {
  connect() {
    this.chordPrefix = null;
    this.chordTimer = null;
    this.onKeyDown = (e) => this.handle(e);
    document.addEventListener("keydown", this.onKeyDown);
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeyDown);
    clearTimeout(this.chordTimer);
  }

  handle(e) {
    const mod = e.metaKey || e.ctrlKey;

    // Always-on shortcuts (work even while typing).
    if (mod && (e.key === "k" || e.key === "K")) {
      e.preventDefault();
      this.openPalette();
      return;
    }
    if (e.key === "Escape") {
      this.closeAllModals();
      return;
    }
    if ((mod && e.key === "?") || (mod && e.key === "/" && e.shiftKey)) {
      // Ctrl/Cmd+? on US/intl keymaps where ? sits on Shift+/.
      e.preventDefault();
      this.toggleHelp();
      return;
    }

    if (isTyping(e.target)) return;
    if (mod || e.altKey) return; // ignore other modified keys

    if (e.key === "?") {
      e.preventDefault();
      this.toggleHelp();
      return;
    }
    if (e.key === "/") {
      // The navbar "search" is now a button that opens the palette;
      // "/" mirrors that, GitHub-style.
      e.preventDefault();
      this.openPalette();
      return;
    }

    // Chord: g, then a navigation key.
    if (this.chordPrefix === "g") {
      this.chordPrefix = null;
      clearTimeout(this.chordTimer);
      const dest = NAV_CHORDS[e.key];
      if (dest) {
        e.preventDefault();
        if (window.Turbo) Turbo.visit(dest); else window.location.href = dest;
      }
      return;
    }
    if (e.key === "g") {
      this.chordPrefix = "g";
      this.chordTimer = setTimeout(() => { this.chordPrefix = null; }, 1200);
      return;
    }
  }

  toggleHelp() {
    const m = document.getElementById("shortcut-help");
    if (!m) return;
    // Close any other modal first so two don't stack.
    if (m.classList.contains("hidden")) this.closeAllModals();
    m.classList.toggle("hidden");
  }

  closeHelp() {
    document.getElementById("shortcut-help")?.classList.add("hidden");
  }

  openPalette() {
    const p = document.getElementById("cmd-palette");
    if (!p) return;
    this.closeAllModals();
    p.classList.remove("hidden");
    // Dispatch a custom event so the palette's own controller can
    // clear stale state without us coupling to it.
    p.dispatchEvent(new CustomEvent("palette:open"));
    // Focus the palette input here, *outside* the cross-controller
    // event path. Doing it inside the palette's handleOpen was
    // unreliable — by the time the rAF callback fired the activation
    // window for the user's keypress had passed, and the focus()
    // call no-op'd. Doing it directly inside the keypress handler
    // (this method is called synchronously from the Ctrl/Cmd+K
    // keydown) keeps focus() within the user-activation window so
    // browsers don't reject it as a programmatic focus-grab.
    const input = p.querySelector("input");
    if (input) {
      input.focus();
      // Belt-and-braces: re-assert focus after the next paint in case
      // layout-timing of the modal reveal eats the first attempt.
      requestAnimationFrame(() => input.focus());
    }
  }

  closeAllModals() {
    document.querySelectorAll("[data-modal]").forEach((m) => m.classList.add("hidden"));
  }
}

// `g _` chord destinations. Deliberately a short list of frequent pages —
// not every route belongs here. Adding one also needs a <dt>/<dd> row in the
// help modal in base.html, or the binding is undiscoverable.
//
// Note this is separate from the ⌘/Ctrl+K palette's NAV_ITEMS in
// cmd_palette_controller.js: the palette should list every page, this only
// the hot ones. See AGENTS.md → Templates.
const NAV_CHORDS = {
  e: "/explorations",
  p: "/pull-requests",
  v: "/",
  n: "/explorations/new",
  r: "/runs",
  w: "/workers",
};

function isTyping(target) {
  if (!target) return false;
  if (target instanceof HTMLInputElement) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  if (target.isContentEditable) return true;
  return false;
}
